// ═══════════════════════════════════════════════════════════════════════════
// ADMIN: PARAMÉTRAGE DE LA MASCOTTE « Professeur Ornaxion »
// ═══════════════════════════════════════════════════════════════════════════
//
// 3 blocs (cf. docs/SPEC_MASCOTTE_ORNAXION.md §4) :
//   1. Apparence  — rig paramétrique + aperçu live + nom affiché.
//   2. Paramétrage — fréquence d'apparition (%) et cooldown (min), tenant.
//   3. Phrases par cas — phrases système (lecture seule) + spécifiques (CRUD).
// Gardé par `mascot.write` (route). GET ouverts (référentiel).

import { buildMascotSvg } from '../domain/mascot-rig.js';
import { ensureMascotStyles } from './component-mascot.js';
import { buildMascotAdminHtml } from './page-admin-mascot-html.js';

export interface MascotSettingsData {
  display_name: string;
  frequency_percent: number;
  cooldown_minutes: number;
  display_style: 'peek' | 'toast';
  appearance: Record<string, unknown>;
}
export interface TriggerCase {
  code: string;
  label: string;
}
export interface PhraseData {
  id: number;
  trigger_case: string;
  text: string;
  is_system: boolean;
}

pages.adminMascot = async function () {
  setHeader(t('mascotAdmin.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [settings, cases, phrases] = await Promise.all([
      api.getMascotSettings() as Promise<MascotSettingsData>,
      api.getMascotTriggerCases() as Promise<TriggerCase[]>,
      api.getMascotPhrases() as Promise<PhraseData[]>,
    ]);
    renderMascotAdmin(settings, cases, phrases);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('mascotAdmin.err.load');
    setContent(`<div class="error" style="padding:16px">${esc(msg)}</div>`);
  }
};

function currentAppearanceFromForm(): Record<string, string> {
  const val = (id: string): string =>
    (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value || '';
  return {
    skin: val('m-skin'),
    hair: val('m-hair'),
    accent: val('m-accent'),
    shirt: val('m-shirt'),
    facialColor: val('m-facialColor'),
    hairStyle: val('m-hairStyle'),
    glasses: val('m-glasses'),
    facial: val('m-facial'),
    neck: val('m-neck'),
  };
}

function refreshPreview(): void {
  const host = document.getElementById('m-preview');
  if (host) host.innerHTML = buildMascotSvg(currentAppearanceFromForm());
}

function renderMascotAdmin(
  settings: MascotSettingsData,
  cases: TriggerCase[],
  phrases: PhraseData[],
): void {
  ensureMascotStyles();
  setContent(buildMascotAdminHtml(settings, cases, phrases));

  refreshPreview();
  [
    'm-hairStyle',
    'm-glasses',
    'm-facial',
    'm-neck',
    'm-skin',
    'm-hair',
    'm-accent',
    'm-shirt',
    'm-facialColor',
  ].forEach((id) => document.getElementById(id)?.addEventListener('input', refreshPreview));

  // ── Enregistrement apparence + paramétrage ──────────────────────────────
  document.getElementById('m-settings-save')?.addEventListener('click', () => {
    const btn = document.getElementById('m-settings-save') as HTMLButtonElement | null;
    const err = document.getElementById('m-settings-err');
    if (err) err.textContent = '';
    const freq = parseInt(
      (document.getElementById('m-frequency') as HTMLInputElement | null)?.value || '0',
      10,
    );
    const cooldown = parseInt(
      (document.getElementById('m-cooldown') as HTMLInputElement | null)?.value || '0',
      10,
    );
    const name =
      (document.getElementById('m-display-name') as HTMLInputElement | null)?.value.trim() || '';
    if (!Number.isInteger(freq) || freq < 0 || freq > 100) {
      if (err) err.textContent = t('mascotAdmin.err.frequency');
      return;
    }
    if (!Number.isInteger(cooldown) || cooldown < 0) {
      if (err) err.textContent = t('mascotAdmin.err.cooldown');
      return;
    }
    if (!name) {
      if (err) err.textContent = t('mascotAdmin.err.name');
      return;
    }
    const displayStyle =
      (document.getElementById('m-display-style') as HTMLSelectElement | null)?.value === 'toast'
        ? 'toast'
        : 'peek';
    if (!btn) return;
    void api.withButton(btn, async () => {
      try {
        await api.updateMascotSettings({
          display_name: name,
          frequency_percent: freq,
          cooldown_minutes: cooldown,
          display_style: displayStyle,
          appearance: currentAppearanceFromForm(),
        });
        window.OrnaxionMascot?.invalidateCache();
        toast(t('mascotAdmin.toast.saved'));
      } catch (ex: unknown) {
        if (err) err.textContent = ex instanceof Error ? ex.message : t('common.error');
      }
    });
  });

  // ── Phrases spécifiques : ajout / modification / suppression ─────────────
  const reload = (): void => {
    void pages.adminMascot?.({} as never);
  };

  document.querySelectorAll<HTMLButtonElement>('.m-newphrase-add').forEach((btn) => {
    btn.addEventListener('click', () => {
      const code = btn.dataset['case'] || '';
      const ta = document.querySelector<HTMLTextAreaElement>(`.m-newphrase[data-case="${code}"]`);
      const text = ta?.value.trim() || '';
      if (!text) return;
      void api.withButton(btn, async () => {
        try {
          await api.createMascotPhrase({ trigger_case: code, text });
          toast(t('mascotAdmin.toast.phraseAdded'));
          reload();
        } catch (ex: unknown) {
          toast(ex instanceof Error ? ex.message : t('common.error'), 'error');
        }
      });
    });
  });

  document.querySelectorAll<HTMLLIElement>('li[data-phrase-id]').forEach((li) => {
    const id = li.dataset['phraseId'];
    if (!id) return;
    li.querySelector<HTMLButtonElement>('.m-phrase-save')?.addEventListener('click', (ev) => {
      const btn = ev.currentTarget as HTMLButtonElement;
      const text = li.querySelector<HTMLTextAreaElement>('.m-phrase-text')?.value.trim() || '';
      if (!text) return;
      void api.withButton(btn, async () => {
        try {
          await api.updateMascotPhrase(id, { text });
          toast(t('mascotAdmin.toast.saved'));
        } catch (ex: unknown) {
          toast(ex instanceof Error ? ex.message : t('common.error'), 'error');
        }
      });
    });
    li.querySelector<HTMLButtonElement>('.m-phrase-del')?.addEventListener('click', (ev) => {
      const btn = ev.currentTarget as HTMLButtonElement;
      void api.withButton(btn, async () => {
        try {
          await api.deleteMascotPhrase(id);
          toast(t('mascotAdmin.toast.phraseDeleted'));
          reload();
        } catch (ex: unknown) {
          toast(ex instanceof Error ? ex.message : t('common.error'), 'error');
        }
      });
    });
  });
}

export {};
